import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Theme contract (unchanged):
 *   localStorage.theme_mode = 'light' | 'dark'
 *   body[data-theme]       = 'light' | 'dark'
 *
 * A single provider keeps every route and every nav in sync. `useTheme` still
 * works standalone (falls back to local state) so older screens that are not
 * under the provider keep functioning.
 */
const ThemeContext = createContext(null);

function readTheme() {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem('theme_mode');
  if (saved === 'light' || saved === 'dark') return saved;
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.body.setAttribute('data-theme', theme);
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem('theme_mode', theme);
    } catch {
      /* storage may be unavailable */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next === 'device') {
      const prefersDark =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      setThemeState(prefersDark ? 'dark' : 'light');
      return;
    }
    if (next === 'light' || next === 'dark') setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export default function useTheme() {
  const ctx = useContext(ThemeContext);
  const [localTheme, setLocalTheme] = useState(readTheme);

  useEffect(() => {
    if (ctx) return;
    applyTheme(localTheme);
    try {
      localStorage.setItem('theme_mode', localTheme);
    } catch {
      /* ignore */
    }
  }, [localTheme, ctx]);

  const setLocal = useCallback((next) => {
    if (next === 'light' || next === 'dark') setLocalTheme(next);
  }, []);

  const toggleLocal = useCallback(() => {
    setLocalTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const fallback = useMemo(
    () => ({ theme: localTheme, setTheme: setLocal, toggleTheme: toggleLocal }),
    [localTheme, setLocal, toggleLocal],
  );

  return ctx || fallback;
}
